import { useState, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function ZoomPage({ onMeetingImported }) {
  const [status, setStatus] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);
  const [imported, setImported] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    // Handle OAuth redirect back from Zoom
    const params = new URLSearchParams(window.location.search);
    if (params.get('zoom_connected')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('zoom_error')) {
      setError(decodeURIComponent(params.get('zoom_error')));
      window.history.replaceState({}, '', window.location.pathname);
    }

    fetch(`${BASE}/api/zoom/status`).then(r => r.json()).then(data => {
      setStatus(data);
      if (data.connected) loadRecordings();
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const loadRecordings = async () => {
    try {
      const res = await fetch(`${BASE}/api/zoom/recordings`);
      const data = await res.json();
      setRecordings(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleImport = async (meeting) => {
    setImporting(meeting.uuid); setError('');
    try {
      const res = await fetch(`${BASE}/api/zoom/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meeting_uuid: meeting.uuid, topic: meeting.topic }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setImported(prev => ({ ...prev, [meeting.uuid]: data.meeting_id }));
      onMeetingImported(data.meeting_id);
    } catch (e) { setError(e.message); }
    finally { setImporting(null); }
  };

  const handleConnect = () => {
    window.location.href = `${BASE}/api/zoom/connect`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Zoom?')) return;
    await fetch(`${BASE}/api/zoom/disconnect`, { method: 'DELETE' });
    setStatus({ connected: false });
    setRecordings([]);
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const fmtDur  = (m) => !m ? '—' : m < 60 ? `${m}m` : `${Math.floor(m/60)}h ${m%60}m`;

  if (loading) return (
    <div className="page fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 80, borderRadius: 12 }} />)}
    </div>
  );

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Zoom Import</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Pull transcripts from your Zoom cloud recordings</div>
        </div>
        {status?.connected && (
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button className="btn btn-outline btn-sm" onClick={loadRecordings}>↻ Refresh</button>
            <button className="btn btn-danger btn-sm" onClick={handleDisconnect}>Disconnect</button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,68,85,0.25)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', color: 'var(--red)', fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {/* Not configured */}
      {!status?.connected && (
        <div>
          {/* Connect button */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <button className="btn btn-primary btn-lg" onClick={handleConnect}>
              🔗 Connect Zoom Account
            </button>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: '0.75rem', fontFamily: 'var(--mono)' }}>
              You will be redirected to Zoom to authorise access
            </div>
          </div>


        </div>
      )}

      {/* Connected */}
      {status?.connected && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', background: 'var(--accent-dark)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: 10, marginBottom: '1.25rem' }}>
            <span style={{ color: 'var(--accent)', fontSize: 18 }}>✓</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>Zoom Connected</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{status.email} · Last 30 days of recordings</div>
            </div>
          </div>

          {recordings.length === 0 ? (
            <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, padding: '4rem 2rem', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: '1rem' }}>📹</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>No recordings found</div>
              <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 320, margin: '0 auto' }}>
                No cloud recordings in the last 30 days, or transcription is not enabled in your Zoom settings.
              </div>
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-3)', marginTop: '1rem', background: 'var(--bg-3)', display: 'inline-block', padding: '8px 16px', borderRadius: 6 }}>
                Zoom Settings → Recording → Cloud Recording → Audio transcript ✓
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{recordings.length} recording{recordings.length !== 1 ? 's' : ''} available</div>
              {recordings.map(m => {
                const isImporting = importing === m.uuid;
                const isImported = !!imported[m.uuid];
                return (
                  <div key={m.uuid} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{m.topic}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        {fmtDate(m.start_time)}
                        {m.duration ? ` · ${fmtDur(m.duration)}` : ''}
                        {m.participant_count ? ` · ${m.participant_count} participants` : ''}
                        {!m.has_transcript && <span style={{ color: 'var(--yellow)', marginLeft: 8 }}>⚠ No transcript</span>}
                      </div>
                    </div>
                    {isImported ? (
                      <button className="btn btn-outline btn-sm" style={{ color: 'var(--accent)', borderColor: 'rgba(0,255,136,0.3)', flexShrink: 0 }} onClick={() => onMeetingImported(imported[m.uuid])}>
                        ✓ View Results
                      </button>
                    ) : (
                      <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} disabled={!m.has_transcript || isImporting} onClick={() => handleImport(m)}>
                        {isImporting ? '⟳ Importing...' : '↓ Import & Analyse'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
